import { WebCodeSend } from '@repo/common-components/business-components/sendSms/api'
import {
  HAS_EXTEND_PROCESS_KEY,
  HAS_NO_EXTEND_PROCESS_VALUE,
  LOGINTIME_KEY,
  RND_KEY,
  SESSION,
  UIN,
} from '@repo/common-utils/constants/user'
import {
  clearSkillhubLeadAuth,
  clearUcAndSkillhubLeadStorage,
  clearUcSessionStorageKeys,
  hasUcSession,
  setSkillhubLeadAuth,
} from '@repo/common-utils/utils/ucSessionAndLeadStorage'
import { loginEncrypt } from '@repo/common-utils/utils/crypto'
import { getBaseUrl } from '@repo/common-utils/utils/url'
import { post } from '@repo/http/http'
import { clearSkillhubAuthSnapshot } from '@/utils/skillhubSessionBackup'

const { VUE_APP_UC = '' } = import.meta.env
const baseUrl = getBaseUrl(VUE_APP_UC)

export type SendCodeResult =
  | { ok: true; salt: string }
  | { ok: false; reason: 'repeat' | 'failed' }

/** 短信发码（scene 10 / type 1）；captcha 为腾讯滑块 ticket + 空格 + randstr */
export async function sendLoginSmsCode(
  areaCode: string,
  phone: string,
  captcha: string,
): Promise<SendCodeResult> {
  const value = `${areaCode}${phone}`
  try {
    const res = (await WebCodeSend({
      scene: 10,
      type: 1,
      value,
      areaCode,
      captcha,
    })) as {
      data: {
        result?: { salt: string }
        error?: { code: number }
      }
    }
    const { result, error } = res.data
    if (result?.salt) {
      return { ok: true, salt: result.salt }
    }
    if (error?.code === 110112) {
      return { ok: false, reason: 'repeat' }
    }
    return { ok: false, reason: 'failed' }
  } catch {
    throw new Error('network')
  }
}

export type LoginWebResult =
  | { ok: true; mode: 'uc' | 'lead' }
  | {
      ok: false
      reason: 'invalid' | 'network' | 'error'
      message?: string
    }

/**
 * 短信验证码登录 / 注册：WebCodeSend 取 salt 后，仅此接口完成会话下发。
 * TODO(UC): 当 Skill 渠道在 LoginWeb 上可免 2FA、直接返回 session 时，可收拢/删除 `lead` 分支，统一走真 UC 会话。
 */
export async function loginWeb(
  areaCode: string,
  phone: string,
  code: string,
  salt: string,
): Promise<LoginWebResult> {
  const value = `${areaCode}${phone}`
  const { A1, rndkey } = loginEncrypt(value, code, salt)
  try {
    const res = (await post(`${baseUrl}/uc/v1/LoginWeb`, {
      data: {
        id: value,
        salt,
        ticket: A1,
        inviteeId: null,
      },
      encrypt: 0,
      noEncTag: true,
    })) as {
      data: {
        result?: {
          session?: string
          uin?: number
          faId?: string
          scene?: number
          session3?: string
          expire?: number
        }
        error?: { code?: number; msg?: string }
      }
    }
    const { result, error } = res.data
    if (error) {
      return {
        ok: false,
        reason: 'error',
        message: typeof error.msg === 'string' ? error.msg : undefined,
      }
    }
    if (!result) {
      return { ok: false, reason: 'invalid' }
    }

    const session = result.session
    const uin = result.uin
    // uin 有效性判断合并到一处：uc 与 lead 分支共用，避免重复条件
    const uinValue = uin != null && Number(uin) !== 0 ? String(uin) : ''

    if (session && uinValue) {
      clearSkillhubLeadAuth()
      localStorage.setItem(RND_KEY, rndkey)
      localStorage.setItem(SESSION, session)
      localStorage.setItem(UIN, uinValue)
      if (result.session3) {
        localStorage.setItem('session3', result.session3)
      }
      localStorage.setItem(LOGINTIME_KEY, String(Date.now()))
      localStorage.setItem(HAS_EXTEND_PROCESS_KEY, HAS_NO_EXTEND_PROCESS_VALUE)
      return { ok: true, mode: 'uc' }
    }

    /* UC 要求 2FA 等、未下发可用 session 时，Skill 仅作引流展示，不落完整 UC 会话；
       但 LoginWeb 仍返回 uin，写入 localStorage 供 apiKey 接口 X-uin 鉴权使用。
       clearUcSessionStorageKeys() 会清除 UIN，故在其之后用统一的 uinValue 回填。 */
    if (result.faId) {
      clearUcSessionStorageKeys()
      if (uinValue) {
        localStorage.setItem(UIN, uinValue)
      }
      setSkillhubLeadAuth(areaCode, phone)
      return { ok: true, mode: 'lead' }
    }

    return { ok: false, reason: 'invalid' }
  } catch {
    return { ok: false, reason: 'network' }
  }
}

/** 清除本地 UC 登录态（与 http 约定一致） */
export function clearUcSessionLocal(): void {
  clearUcSessionStorageKeys()
}

/** UC 注销并清理本地存储；仅引流态时不调 UC Logout。 */
export async function logoutUc(): Promise<void> {
  if (hasUcSession()) {
    try {
      await post(`${baseUrl}/uc/v1/Logout`, {
        data: {},
        encrypt: 0,
        noEncTag: true,
      })
    } catch {
      /* 仍清理本地，避免残留 */
    }
  }
  clearUcAndSkillhubLeadStorage()
  clearSkillhubAuthSnapshot()
}
